import React, { forwardRef } from "react";
import styled from "styled-components";

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.span`
  color: rgb(33, 37, 67);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.haspoundsign ? "0" : "8px")};
  border: solid 1px
    ${(props) => (props.error ? "rgb(220, 38, 38)" : "rgb(224, 228, 244)")};
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "rgb(245, 245, 245)" : "white")};
  padding: 0 16px;
  height: 42px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: rgb(97, 98, 197);
  }
`;

const PoundSign = styled.span`
  color: rgb(33, 37, 67);
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  color: ${(props) =>
    props.disabled ? "rgb(150,150,150)" : "rgb(33, 37, 67)"};
  background: transparent;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "text")};
  &::placeholder {
    color: rgb(179, 182, 195);
  }
`;

const Icon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
  text-align: start;
`;

const Required = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
`;

const Input = forwardRef(({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  icon,
  leftIcon,
  onIconClick,
  error,
  errorMessage,
  required = false,
  disabled = false,
  showPoundSign = false,
  defaultValue,
  id,
  name,
  enableTracking = false, // New prop to control tracking
  ...props
}, ref) => {
  // Show pound sign only when there's a value and showPoundSign is true
  const shouldShowPoundSign =
    showPoundSign && value && value.toString().length > 0;

  // GTM Tracking Handlers - only fire if enableTracking is true
  const handleFocus = (e) => {
    if (!enableTracking) return;
    
    window.dataLayer = window.dataLayer || [];
    
    window.dataLayer.push({
      event: "signup_field_focus",
      field_id: e.target.id || "",
      field_name: e.target.name || "",
      field_label: label || "",
      field_type: type,
      page_section: "signup_form",
    });
  };

  const handleBlur = (e) => {
    if (!enableTracking) return;
    
    window.dataLayer = window.dataLayer || [];
    
    // Only push if there's a value
    if (e.target.value) {
      window.dataLayer.push({
        event: "signup_field_complete",
        field_id: e.target.id || "",
        field_name: e.target.name || "",
        field_label: label || "",
        field_type: type,
        value_length: e.target.value.length,
        has_value: true,
        page_section: "signup_form",
      });
    } else {
      // Track abandonment (focused but left empty)
      window.dataLayer.push({
        event: "signup_field_abandoned",
        field_id: e.target.id || "",
        field_name: e.target.name || "",
        field_label: label || "",
        field_type: type,
        has_value: false,
        page_section: "signup_form",
      });
    }
  };

  // Combine with existing onChange if provided
  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <InputContainer>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}
      <InputWrapper
        error={error}
        disabled={disabled}
        haspoundsign={shouldShowPoundSign}
      >
        {leftIcon && (
          <Icon src={leftIcon} onClick={onIconClick} alt="input icon" />
        )}
        {shouldShowPoundSign && <PoundSign>£</PoundSign>}
        
        <StyledInput
          ref={ref}
          defaultValue={defaultValue}
          id={id}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          {...props}
        />

        {icon && <Icon src={icon} onClick={onIconClick} alt="input icon" />}
      </InputWrapper>

      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </InputContainer>
  );
});

export default Input;